import {
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import * as XLSX from 'xlsx';
import { AbstractComponent } from '../../abstract.component';
import {
  DEFAULT_MAX_BASE_POINTS,
  Lookups,
  ScoringDefinition,
  ScoringMethod
} from '@model';
import { TranslocoService } from '@ngneat/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { required } from '@utility';
import { FileType } from '../../drag-and-drop/drag-and-drop.component';

@Component({
  selector: 'app-scoring-definition-edit-modal',
  templateUrl: './scoring-definition-edit-modal.component.html',
  styleUrl: './scoring-definition-edit-modal.component.scss',
  standalone: false
})
export class ScoringDefinitionEditModalComponent extends AbstractComponent {

  @Input() scoringDefinition?: ScoringDefinition;
  @Input() scoringMethods: Lookups[] = [];
  @Input() rankingMethods: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() disableEditing: boolean = true;

  @Output() onSave = new EventEmitter<ScoringDefinition>();

  inputForm!: FormGroup;

  positionCalculationTypes = [
    PositionPointsCalculationType.MANUAL,
    PositionPointsCalculationType.EXCEL,
    PositionPointsCalculationType.STEP,
    PositionPointsCalculationType.PERCENT
  ];

  constructor(
      public translocoService: TranslocoService, public activeModal: NgbActiveModal, private formBuilder: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.buildForm();
    this.patchFromModel();
    if (!this.disableEditing) {
      this.registerSubscriptions();
    }
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private buildForm(): void {
    this.inputForm = this.formBuilder.group({
      name: [null, required],
      scoringMethod: [null, required],
      maxBasePoints: [DEFAULT_MAX_BASE_POINTS, required],
      measurementPrecisionType: [{ value: null, disabled: true }],
      rankingMethod: [null, required],
      positionScoring: this.formBuilder.group({
        maxPosition: null,
        calculationType: null,
        stepValue: null,
        percentValue: null,
        positions: this.formBuilder.array([]),
        xlsx: null
      })
    });

    this.positionScoring.addValidators(this.positionScoringValidator());
    this.positionScoring.disable({ emitEvent: false });
  }

  private patchFromModel(): void {
    if (!this.scoringDefinition) return;
    this.inputForm.patchValue(
        {
          name: this.scoringDefinition.name,
          scoringMethod: this.scoringDefinition.scoringMethod,
          maxBasePoints: this.scoringDefinition.maxBasePoints,
          rankingMethod: this.scoringDefinition.rankingMethod
        },
        { emitEvent: false }
    );

    if (
        this.scoringDefinition.scoringMethod === ScoringMethod.POSITION_BASED &&
        this.scoringDefinition.positionScoring?.length
    ) {
      this.positionScoring.enable({ emitEvent: false });
      this.positionScoring
          .get('calculationType')
          ?.setValue(PositionPointsCalculationType.MANUAL, { emitEvent: false });

      this.positions.clear({ emitEvent: false });

      this.scoringDefinition.positionScoring.forEach(p => {
        this.positions.push(
            this.formBuilder.group({
              position: [p.position, [Validators.required, Validators.min(2)]],
              points: [
                p.points,
                [ Validators.required, Validators.min(0), Validators.max(this.inputForm.get('maxBasePoints')!.value)]
              ]
            }),
            { emitEvent: false }
        );
      });
    }
    else if (this.scoringDefinition.scoringMethod === ScoringMethod.RELATIVE_TIME_TO_FIRST) {
      this.enableField(this.inputForm.get('measurementPrecisionType')! as FormControl, true, this.scoringDefinition.measurementPrecisionType);
    }
  }

  private registerSubscriptions(): void {
    this.subscriptions.push(
        this.inputForm.get('scoringMethod')!.valueChanges.subscribe(method => {
          this.positions.clear();
          this.positions.removeValidators([required]);
          this.positionScoring.disable();

          if (method === ScoringMethod.RELATIVE_TIME_TO_FIRST) {
            this.enableField(this.inputForm.get('measurementPrecisionType')! as FormControl, true);
          }

          if (method === ScoringMethod.POSITION_BASED) {
            this.disableField(this.inputForm.get('measurementPrecisionType')! as FormControl);
            this.positionScoring.enable();
            this.positionScoring
                .get('calculationType')
                ?.setValue(PositionPointsCalculationType.MANUAL);
            this.positions.setValidators([required]);
          }
          this.positionScoring.updateValueAndValidity();
        })
    );

    this.subscriptions.push(
        this.positionScoring
            .get('calculationType')!
            .valueChanges.subscribe(type => this.initScoringForm(type))
    );

    this.subscriptions.push(
        this.inputForm.get('maxBasePoints')!.valueChanges.subscribe(() =>
            this.positionScoring.updateValueAndValidity()
        )
    );
  }

  initScoringForm(type: PositionPointsCalculationType): void {
    this.positions.clear();

    this.disableField(this.positionScoring.get('stepValue') as FormControl);
    this.disableField(this.positionScoring.get('percentValue') as FormControl);
    this.disableField(this.positionScoring.get('maxPosition') as FormControl);
    if (!this.positions.length && type === PositionPointsCalculationType.MANUAL) {
      this.onAddPositionPoint();
    }

    if (type === PositionPointsCalculationType.STEP) {
      this.enableField(this.positionScoring.get('stepValue') as FormControl, true);
      this.enableField(this.positionScoring.get('maxPosition') as FormControl, true);
    }

    if (type === PositionPointsCalculationType.PERCENT) {
      this.enableField(this.positionScoring.get('percentValue') as FormControl, true);
      this.enableField(this.positionScoring.get('maxPosition') as FormControl, true);
    }
  }

  generatePositionScoring(): void {
    const maxPosition = this.positionScoring.get('maxPosition')!.value;
    const type = this.positionScoring.get('calculationType')!.value;
    const step = this.positionScoring.get('stepValue')!.value;
    const percent = this.positionScoring.get('percentValue')!.value;
    const base = this.inputForm.get('maxBasePoints')!.value;

    this.positions.clear();

    if (!base || !maxPosition) return;

    for (let i = 2; i <= maxPosition; i++) {
      let points = base;

      if (type === PositionPointsCalculationType.STEP) {
        points = Math.max(base - (i - 1) * step, 0);
      }

      if (type === PositionPointsCalculationType.PERCENT) {
        points = Math.round(base * Math.pow(1 - percent / 100, i - 1));
      }

      this.positions.push(
          this.formBuilder.group({
            position: [i],
            points: [points, Validators.min(0)]
          })
      );
    }
  }

  uploadExcel(dataUrl?: string): void {
    this.positions.clear();
    if (!dataUrl) return;

    const base64 = dataUrl.split(',')[1];
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));

    const workbook = XLSX.read(bytes, { type: 'array'});
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });

    rows.forEach(r => {
      let position = r[0];
      let points = r[1];

      position = typeof position === 'string' ? parseFloat(position) : position;
      points = typeof points === 'string' ? parseFloat(points) : points;
      if (!Number.isFinite(position) || !Number.isFinite(points)) return;

      this.positions.push(
          this.formBuilder.group({
            position: [position, [Validators.required, Validators.min(2)]],
            points: [points, [Validators.required, Validators.min(0)]]
          })
      );
    });
    this.markAllAsTouched(this.positionScoring);
  }

  onAddPositionPoint(): void {
    const last = this.positions.length
        ? this.positions.at(this.positions.length - 1).get('position')!.value
        : 1;

    this.positions.push(
        this.formBuilder.group({
          position: [last + 1, [Validators.required, Validators.min(2)]],
          points: [null, required]
        })
    );
  }

  onDeleteBonusPoint(index: number): void {
    if (this.disableEditing) return;
    this.positions.removeAt(index);
    if (!this.positions.length) this.onAddPositionPoint();
  }

  onSaveButtonClick(): void {
    if (this.disableEditing) return;
    if (this.inputForm.invalid) {
      this.markAllAsTouched(this.inputForm);
      return;
    }

    const def: ScoringDefinition = {
      ...this.scoringDefinition!,
      ...this.inputForm.getRawValue()
    };

    if (def.scoringMethod === ScoringMethod.POSITION_BASED) {
      def.positionScoring = this.positions.controls.map(c => ({
        position: Number(c.get('position')!.value),
        points: Number(c.get('points')!.value)
      }));
    } else {
      def.positionScoring = undefined;
    }

    this.onSave.emit(def);
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  get positionScoring(): FormGroup {
    return this.inputForm.get('positionScoring') as FormGroup;
  }

  get positions(): FormArray {
    return this.positionScoring.get('positions') as FormArray;
  }

  trackByPosition(_: number, ctrl: AbstractControl): number {
    return ctrl.value.position;
  }

  private positionScoringValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const positions = (control.get('positions') as FormArray)?.controls;
      if (!positions?.length) return null;

      const max = Number(this.inputForm.get('maxBasePoints')!.value);
      const seen = new Map<number, number>();
      const errors: any = {};

      positions.forEach((c, i) => {
        const p = Number(c.get('position')!.value);
        const pts = Number(c.get('points')!.value);

        if (p < 2) (errors.invalidStartIndexes ??= []).push(i);
        if (pts > max) (errors.exceedsMaxPointsIndexes ??= []).push(i);
        if (seen.has(p)) {
          (errors.duplicatePositionIndexes ??= []).push(i, seen.get(p)!);
        } else {
          seen.set(p, i);
        }
      });

      return Object.keys(errors).length ? errors : null;
    };
  }

  protected readonly PositionPointsCalculationType = PositionPointsCalculationType;
  protected readonly ScoringMethod = ScoringMethod;
  protected readonly FileType = FileType;
}

export enum PositionPointsCalculationType {
  STEP = 1,
  PERCENT = 2,
  EXCEL = 3,
  MANUAL = 4
}
